
import { Component, Vue } from "vue-property-decorator";
import { SMUser, SMTasks, SMCases } from "@/store";
import { EMPLOYER_ROLE } from "@/constants/roles";
import { EMPLOYEES_TASKS_NAME, EMPLOYER_TASKS_NAME } from "@/constants/tasks";
import { TTask_Table, TActions } from "@/models/tasks";

import { RCandidateServices } from "@/modules/Services/RServices";

const CASE_PRODUCT_TYPE = "case";
const COMPLIANCE_SERVICE = "Compliance";

const VALUE_NAME = "name";
const VALUE_STATUS = "status";
const VALUE_SECTION = "complianceSection";
const VALUE_ACTION_BY = "actionBy";
const VALUE_DUE_DATE = "dueDate";

const TABLE_VERSION = 2;

const USE_LOAD_ALL = true;

Component.registerHooks(["beforeRouteEnter"]);

@Component({
  components: {
    Loader: () => import("@/components/Loader/Loader.vue"),
    ToDoTasksTableTabs: () => import("@/modules/Tasks/ToDoTasksTable/ToDoTasksTableTabs.vue"),
    ComplianceExpiredWidget: () => import("@/modules/Compliance/ComplianceExpiredWidget.vue"),
  },
})
export default class Browse extends Vue {
  role = EMPLOYER_ROLE;

  TABLE_VERSION = TABLE_VERSION;

  actions: TActions = {
    delete: this.deleteTask,
  };

  get availableActions(): TActions {
    if (SMUser.isExpert) return this.actions;

    const hide = (SMUser.isEmployee || SMUser.isEmployer) && !SMCases.caseId;

    if (hide) return {};

    return this.actions;
  }

  get descriptionExpiredWidget() {
    return this.userIsEmployer
      ? this.$tc("messages.employerDescription")
      : this.$tc("messages.employeeDescription");
  }

  get isComplianceBlocked() {
    return SMUser.user?.is_compliance_blocked;
  }

  get userIsEmployer() {
    return SMUser.user?.role === this.role;
  }

  get loading() {
    return SMTasks.loading;
  }

  get companyId(): number {
    return Number(this.$route.params.company_id ?? 0);
  }

  async getEmployersTasks(payload?) {
    const params = payload?.params ?? {};

    const requestParams = {
      list: "employer",
      // offset: this.employerTasks.length,
      // limit: TABLE_LIST_LIMIT,
      ...params,
      all: true,
    };

    const { data } = await SMTasks.getTableTasks(requestParams);

    if (!data) return;

    await SMTasks.setEmployerTasks(data as any, params);
  }

  async getCandidatesTasks(payload?) {
    const params = payload?.params ?? {};

    const requestParams = {
      list: "employee",
      // offset: this.candidateTasks.length,
      // limit: TABLE_LIST_LIMIT,
      ...params,
      all: true,
    };

    const { data } = await SMTasks.getTableTasks(requestParams);

    if (!data) return;

    await SMTasks.setCandidateTasks(data as any, params);
  }

  async getTableTasks(payload?) {
    await this.getEmployersTasks(payload);
    await this.getCandidatesTasks(payload);
  }

  async beforeRouteEnter(to: any, from: any, next: any) {
    next((vm: any) => {
      if (vm.companyId) SMTasks.setCompanyId(vm.companyId);

      vm.getTableTasks();
    });
  }

  mounted() {
    this.$root.$on("update-company-level-data", this.getTableTasks);
  }

  tabValues: string[] = [EMPLOYER_TASKS_NAME, EMPLOYEES_TASKS_NAME];

  get employerTasks() {
    return SMTasks.employerTasks.filter((task) => task.service === COMPLIANCE_SERVICE);
  }

  get candidateTasks() {
    return SMTasks.candidateTasks.filter((task) => task.service === COMPLIANCE_SERVICE);
  }

  // TODO: delete filters when query for services is done on the backend
  // get employerTasksToDo() {
  //   return SMTasks.employerTasksToDo.filter((task) => task.service === COMPLIANCE_SERVICE);
  // }
  //
  // get candidateTasksToDo() {
  //   return SMTasks.candidateTasksToDo.filter((task) => task.service === COMPLIANCE_SERVICE);
  // }
  //
  // get employerTasksArchived() {
  //   return SMTasks.employerTasksArchived.filter((task) => task.service === COMPLIANCE_SERVICE);
  // }
  //
  // get candidateTasksArchived() {
  //   return SMTasks.candidateTasksArchived.filter((task) => task.service === COMPLIANCE_SERVICE);
  // }

  navigateTask(item: TTask_Table) {
    const { id } = item;

    let path = `${SMUser.basePath}/tasks/${id}`;

    if (item?.productType === CASE_PRODUCT_TYPE) {
      path = `/${RCandidateServices.path}/tasks/${id}`;
    }

    if (this.companyId) path = `/expert/companies/${this.companyId}/tasks/${id}`;

    this.$router.push(path);
  }

  async deleteTask(id: number) {
    await SMTasks.archiveTableTask(id);

    await this.$nextTick();

    await this.getTableTasks();
  }

  tabName = "";

  setTabName(value: string) {
    this.tabName = value;
  }

  get tableHeaders() {
    return [
      {
        text: this.$t("table.headers.name"),
        value: VALUE_NAME,
      },
      {
        text: this.$t("table.headers.status"),
        value: VALUE_STATUS,
        slot: true,
        filterable: true,
        multipleSelect: true,
        filterSearch: false,
      },
      {
        text: this.$t("table.headers.complianceSection"),
        value: VALUE_SECTION,
      },
      {
        text: this.$t("table.headers.dueDate"),
        value: VALUE_DUE_DATE,
        hint: this.$t("table.hints.dueDate"),
      },
    ];
  }

  get tableWithActionByColumnHeaders() {
    return [
      {
        text: this.$t("table.headers.name"),
        value: VALUE_NAME,
      },
      {
        text: this.$t("table.headers.status"),
        value: VALUE_STATUS,
        slot: true,
        filterable: true,
        multipleSelect: true,
        filterSearch: false,
      },
      {
        text: this.$t("table.headers.complianceSection"),
        value: VALUE_SECTION,
      },
      {
        text: this.$t("table.headers.actionBy"),
        value: VALUE_ACTION_BY,
        filterable: true,
        multipleSelect: true,
      },
      {
        text: this.$t("table.headers.dueDate"),
        value: VALUE_DUE_DATE,
        hint: this.$tc("table.hints.dueDate"),
      },
    ];
  }

  get headerValues() {
    if (SMUser.isExpert) {
      return this.tabName === EMPLOYER_TASKS_NAME
        ? this.tableHeaders
        : this.tableWithActionByColumnHeaders;
    }

    return this.tabName === EMPLOYER_TASKS_NAME || !SMUser.isExpert
      ? this.tableWithActionByColumnHeaders
      : this.tableHeaders;
  }

  async loadData(payload: any) {
    if (USE_LOAD_ALL) return;

    await this.getTableTasks(payload);
  }
}
